import { getBridgeOrigin, getEmbeddingInfo } from './embedding';
import { pushEvent } from './gtm';

/**
 * Message envelope shared with the listener tag in the marketing GTM container
 * (GTM-5DS7VZ9). The listener validates `source` and `event.origin` before
 * re-pushing anything onto the parent dataLayer — keep both in step if either
 * side changes.
 *
 * That listener whitelists event names and parameter keys, and silently drops
 * anything it does not recognise. Adding an event here therefore means adding
 * it to the container and publishing that first, or the new event is lost
 * without any error.
 */
export const BRIDGE_SOURCE = 'iqd-budget-calc';
export const BRIDGE_VERSION = 1;

export type AnalyticsParams = Record<string, string | number | boolean | null>;

/**
 * - `gtm`    — standalone budget.iqd-pro.com: this app owns its own container.
 * - `bridge` — embedded on a Hi-Level site: events go to the parent so they
 *              share its client id, session and CookieYes consent state.
 * - `off`    — embedded on a distributor site: their page, their consent, and
 *              no listener to receive events. Distributor attribution comes
 *              from `embeddingDomain` on the lead instead.
 */
export type AnalyticsMode = 'gtm' | 'bridge' | 'off';

let mode: AnalyticsMode | null = null;
let bridgeOrigin: string | null = null;

export function getAnalyticsMode(): AnalyticsMode {
  if (mode !== null) return mode;
  const info = getEmbeddingInfo();
  if (!info.isEmbedded) {
    mode = 'gtm';
  } else {
    bridgeOrigin = getBridgeOrigin();
    mode = bridgeOrigin ? 'bridge' : 'off';
  }
  return mode;
}

/**
 * Sends one analytics event by whichever route this embedding context allows.
 *
 * Never pass personally identifying values (email, phone, name) in `params` —
 * they would land in GA4, which is both a policy breach and against Google's
 * terms.
 */
export function trackEvent(event: string, params: AnalyticsParams = {}): void {
  switch (getAnalyticsMode()) {
    case 'gtm':
      pushEvent({ event, ...params });
      break;
    case 'bridge':
      if (!bridgeOrigin) return;
      window.parent.postMessage(
        { source: BRIDGE_SOURCE, version: BRIDGE_VERSION, event, params },
        bridgeOrigin,
      );
      break;
    case 'off':
      break;
  }
}

/**
 * Standalone only. When embedded, the host page already counts its own
 * page_view; forwarding the calculator's internal route changes would inflate
 * the host's pageviews and pollute its landing-page reports.
 */
export function trackPageView(pageName: string, params: AnalyticsParams = {}): void {
  if (getAnalyticsMode() !== 'gtm') return;
  trackEvent('page', { pageName, ...params });
}
